import cx from 'classnames'
import { Field, FieldProps, Form, Formik } from 'formik'
import { KeyboardEvent, useState } from 'react'
import { HandbookItemParams } from 'api/clients/HandbookClient'
import { BaseEmoji, NestedHandbookHandbookItem } from 'types'
import { useHandbookItems } from '../HandbookItemProvider'
import Styles from '../HandbookItems.module.scss'
import TreeRowStyles from 'components/FilesTree/components/TreeRow/TreeRow.module.scss'
import { Icon } from './Components/Icon'
import { useBlurOnNavigateRef } from './hooks'
import { NavItem } from 'components/NavItem'
import { Add, MoreThick } from 'almanac-ui/icons'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useFolder } from 'hooks/queries/folders/useFolder'
import useNewEntrypointModal from 'hooks/useNewEntrypointModal'
import { useCreateArticle } from 'hooks/useCreateArticle'
import { useIsPwa } from 'utils/isPwa'
import QueryString from 'qs'
import { useQueryParams } from 'hooks/useQueryParams'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { computed } from 'mobx'
import { useHandbookPermissions } from 'components/Handbook2_0/hooks'
import { useHandbookRouteMatch } from 'components/Handbook2_0/hooks/useHandbookRouteMatch'
import { useRouteMatch } from 'react-router-dom'
import { FOLDER_VIEWS_PATH } from 'paths'
import { DropdownTrigger } from 'components/DropdownTrigger/DropdownTrigger'
import { ContextMenuTrigger } from 'almanac-ui/ContextMenuTrigger/ContextMenuTrigger'
import { FolderMenuContainer } from 'components/FolderMenu/FolderMenuContainer'
import { FolderPreloader } from 'components/FolderPreloader/FolderPreloader'
import { useUpdateAlmanacFile } from 'hooks/queries/almanacFiles/useUpdateAlmanacFile'
import { HandbookTreeItem } from 'components/Handbook2_0/Stores/HandbookTreeItem'
import { ConditionalWrapper } from 'common/ConditionalWrapper/ConditionalWrapper'
import { ExpandButton } from 'components/NavItem/ExpandButton/ExpandButton'
import { Tooltip } from 'components/Tooltip/Tooltip'

interface Props {
  item: NestedHandbookHandbookItem
  depth?: number
  onExpand: () => void
  showOptions?: Boolean
  isSelected?: Boolean
  isLink?: Boolean
  canExpand?: Boolean
}

export const Folder = observer(
  ({ item, depth = 0, onExpand, showOptions = true, isSelected, isLink = true, canExpand = true }: Props) => {
    const { data } = useFolder(item.folder.key || null, { staleTime: 60 * 1000, cacheTime: 60 * 1000 }) // one minute stale time
    const { openNewEntrypointModal } = useNewEntrypointModal()
    const { createArticleCallback } = useCreateArticle({ openInNewTab: !useIsPwa() })
    const { isMobile } = useResponsiveDesign()
    const { focusView } = useQueryParams()
    const { docStore, files, handbooks } = useRootStore()
    const treeItem = handbooks.items.get(item.id.toString())
    const selectedHandbook = handbooks.get(item.nested_handbook?.key)?.handbook
    const { canEdit } = useHandbookPermissions(selectedHandbook)
    const { handbookKey } = useHandbookRouteMatch()
    const onFolderView = useRouteMatch(FOLDER_VIEWS_PATH)
    const [isRenaming, setIsRenaming] = useState(false)
    const { mutateAsync } = useUpdateAlmanacFile({ key: item.folder.key })

    if (!treeItem?.data.nested_handbook || !treeItem.data.folder) return null

    const {
      data: { nested_handbook, folder, database_mode, title },
      updateAttrs,
      isExpanded,
    } = treeItem
    const expandableHandbook = !!nested_handbook
    const isExpandable = !((!nested_handbook && treeItem.children.length === 0) || !expandableHandbook) && !!canExpand
    const showExpandButton = (isExpandable && data?.folder.has_descendant_articles) || data?.folder.has_subprojects

    const isAncestorOfActiveItem = computed(() => {
      if (database_mode && !!handbookKey && handbookKey === nested_handbook.key && onFolderView) return true

      const { folder } = item
      if (!folder) {
        return false
      }

      const activeAlmanacFileId = docStore?._almanac_file?.id
      if (!activeAlmanacFileId) {
        return false
      }

      const activeAlmanacFile = files.get(activeAlmanacFileId)
      if (!activeAlmanacFile) {
        return false
      }

      return (
        activeAlmanacFile.ancestors.length > 1 &&
        activeAlmanacFile.ancestors[activeAlmanacFile.ancestors.length - 1].id === folder.almanac_file.id
      )
    }).get()

    const updateEmoji = async (emoji: BaseEmoji | null) => {
      if (!folder) {
        return
      }
      const newEmoji = emoji?.native || null
      const currentEmoji = folder.header_emoji
      // Optimistic update
      updateAttrs({ folder: { ...folder, header_emoji: newEmoji } })

      try {
        await mutateAsync({ emoji: newEmoji })
      } catch {
        // Rollback
        updateAttrs({ folder: { ...folder, header_emoji: currentEmoji } })
      }
    }

    const handleAddClick = async () => {
      if (isMobile) {
        // for mobile we don't open a modal just create new file
        createArticleCallback()
        return
      }

      // in case we haven't selected folder we're using the default one
      openNewEntrypointModal({
        createInFolder: data?.folder,
      })
    }

    if (isRenaming) {
      return (
        <div className={Styles.renameWrapper}>
          <FolderForm
            treeItem={treeItem}
            item={item}
            onCancel={() => setIsRenaming(false)}
            onRename={() => setIsRenaming(false)}
          />
        </div>
      )
    }

    const menuPopover = (close: () => void) => (
      <FolderMenuContainer
        id={folder.key}
        onAction={(key) => {
          if (key === 'rename') {
            setIsRenaming(true)
          }
          if (key === 'move') {
            return
          }
          close()
        }}
        dismiss={close}
      />
    )

    const tooltipLabel = () => {
      const action = isExpanded ? 'Hide' : 'Show'

      return `${action} Items`
    }

    return (
      <>
        <FolderPreloader fileId={treeItem.data.folder.key}>
          <ConditionalWrapper
            condition={!!showOptions}
            wrapper={(children) => {
              return (
                <ContextMenuTrigger placement="bottom-start">
                  {children}
                  {menuPopover}
                </ContextMenuTrigger>
              )
            }}
          >
            <div>
              <NavItem.Root
                to={
                  isLink && {
                    pathname: nested_handbook.url,
                    search: QueryString.stringify(
                      {
                        focusView,
                      },
                      { filter: (_, value) => value ?? undefined },
                    ),
                  }
                }
                indentLevel={depth}
                onPress={() => {
                  !isExpanded && onExpand()
                }}
                isActive={!!isSelected}
                isHighlighted={isAncestorOfActiveItem}
              >
                <NavItem.Emoji isEditable={showOptions && canEdit} onChange={updateEmoji} fileKey={item.folder.key}>
                  <Icon item={treeItem.data} isExpanded={isExpanded} />
                </NavItem.Emoji>
                <NavItem.Label label={title || ''} />
                {showOptions && (
                  <NavItem.ControlGroup>
                    {showExpandButton && (
                      <div className={TreeRowStyles.expandButtonTooltipContainer}>
                        <Tooltip label={tooltipLabel()} placement="top">
                          <div className={TreeRowStyles.expandButtonContainer}>
                            <ExpandButton isExpanded={isExpanded} onPress={onExpand} />
                          </div>
                        </Tooltip>
                      </div>
                    )}
                    <DropdownTrigger>
                      <NavItem.Control aria-label="Options" icon={MoreThick} />
                      {menuPopover}
                    </DropdownTrigger>
                    {canEdit && <NavItem.Control icon={Add} onPress={handleAddClick} />}
                  </NavItem.ControlGroup>
                )}
              </NavItem.Root>
            </div>
          </ConditionalWrapper>
        </FolderPreloader>
      </>
    )
  },
)

interface FolderFormProps {
  item: NestedHandbookHandbookItem
  onCancel?: () => void
  onRename?: () => void
  treeItem: HandbookTreeItem
}

function FolderForm({ item, onCancel, onRename, treeItem }: FolderFormProps) {
  const inputEl = useBlurOnNavigateRef<HTMLInputElement>()
  const { currentHandbook: handbook } = useHandbookItems()
  const { mutateAsync } = useUpdateAlmanacFile({ key: item.folder.key })

  if (!handbook) return null

  return (
    <Formik<HandbookItemParams>
      onSubmit={async (values) => {
        treeItem.updateAttrs({ title: values.title })
        try {
          await mutateAsync({ title: values.title })
          onRename?.()
        } catch {
          treeItem.updateAttrs({ title: item.title })
        }
      }}
      initialValues={{ title: item.title }}
      validate={(values) => {
        const errors = {} as { title?: boolean }
        if (!values.title) {
          errors.title = true
        }
        return errors
      }}
    >
      {({ isValid, submitForm }) => (
        <Form>
          <div className={cx(Styles.inputWrapper, { [Styles.error]: !isValid })}>
            <Icon item={item} />
            <Field name="title">
              {({ field }: FieldProps) => (
                <input
                  ref={inputEl}
                  {...field}
                  autoFocus
                  placeholder="Folder name"
                  onKeyDown={(e: KeyboardEvent) => {
                    if (e.key === 'Escape') {
                      onCancel?.()
                    }
                  }}
                  onBlur={(e) => {
                    field.onBlur(e)
                    if (field.value && isValid) {
                      return submitForm()
                    }
                    onCancel?.()
                  }}
                />
              )}
            </Field>
          </div>
        </Form>
      )}
    </Formik>
  )
}
